import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getPrograms, getUniversities } from '../services/api';
import { EdFiLogo, SNPLLogo } from '../components/EdFiLogo';
import { MobileMenuButton, MobileMenu } from '../components/MobileNav';
import {
  Clock, CreditCard, CheckCircle, ArrowRight, Building2,
  TrendingUp, Shield, GraduationCap, Star, Zap,
  FileText, Users, BarChart3, Laptop, Banknote,
  AlertTriangle, Globe, Check, X, Minus, Landmark,
  BadgeCheck, Percent, Timer, Wallet, UserCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';

const LandingPage = () => {
  const { isAuthenticated } = useAuth();
  const [featuredPrograms, setFeaturedPrograms] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [audience, setAudience] = useState('student');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [progsRes, uniRes] = await Promise.all([
          getPrograms({ limit: 6 }),
          getUniversities()
        ]);
        setFeaturedPrograms(progsRes.data.programs || []);
        setUniversities(uniRes.data || []);
      } catch (e) { console.error(e); }
    };
    fetchData();
  }, []);

  const formatPrice = (price) =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 }).format(price);

  const isStudent = audience === 'student';

  const content = {
    student: {
      heroTitle: 'Estudia hoy, paga despues',
      heroSub: 'Financiamiento educativo desde $10,000 hasta $100,000 MXN. Pre-aprobacion instantanea, pagos flexibles y proceso 100% digital.',
      cta: 'Solicitar mi credito',
      ctaLink: '/register',
      secondaryCta: 'Pre-Aprobacion 30 seg',
      secondaryLink: '/pre-aprobacion',
    },
    institution: {
      heroTitle: 'Crece tu matricula sin riesgo',
      heroSub: 'Ofrece financiamiento a tus alumnos sin asumir riesgo crediticio. Recibe el 80% del pago de forma inmediata.',
      cta: 'Afiliar mi institucion',
      ctaLink: '/register',
      secondaryCta: 'Ver como funciona',
      secondaryLink: '#economia',
    },
  };
  const c = content[audience];

  const benchmarkData = [
    { metric: 'Aprobacion', edfi: '+70%', traditional: '<30%', bnpl: '<20%', internal: '100%' },
    { metric: 'Tasa interes', edfi: '5% mensual', traditional: '30% anual', bnpl: '42% anual', internal: '12% anual' },
    { metric: 'Tiempo decision', edfi: 'Instantaneo', traditional: '1-15 dias', bnpl: 'Instantaneo', internal: '~15 dias' },
    { metric: 'Plazo maximo', edfi: '24 meses', traditional: '24 meses', bnpl: '6 meses', internal: '6 meses' },
    { metric: 'Ticket maximo', edfi: '$5,000 USD', traditional: 'Variable', bnpl: '$600 USD', internal: 'Colegiatura' },
    { metric: 'Digitalizacion', edfi: '100%', traditional: 'No', bnpl: 'Si', internal: 'Parcial' },
    { metric: 'Underwriting educativo', edfi: 'Si', traditional: 'No', bnpl: 'No', internal: 'No' },
  ];

  const partners = [
    'Anahuac Online', 'IADA', 'Universidad Panamericana', 'ITAM',
    'UDEM', 'UNIR', 'Instituto BIVA', 'IEBS', 'Emeritus'
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between">
            <EdFiLogo size="md" linkTo="/" />
            <div className="hidden md:flex items-center gap-8">
              <Link to="/programs" className="text-slate-600 hover:text-[#0A2463] transition-colors font-medium" data-testid="nav-programs">Catalogo</Link>
              <Link to="/edupro" className="text-[#E91E8C] hover:text-[#c21473] transition-colors font-semibold" data-testid="nav-edupro">EduPro</Link>
              <Link to="/pre-aprobacion" className="text-slate-600 hover:text-[#0A2463] transition-colors font-medium" data-testid="nav-preapproval">Pre-Aprobacion</Link>
              <Link to="/calculator" className="text-slate-600 hover:text-[#0A2463] transition-colors font-medium" data-testid="nav-calculator">Calculadora</Link>
              <a href="#instituciones" className="text-slate-600 hover:text-[#0A2463] transition-colors font-medium">Instituciones</a>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              {isAuthenticated ? (
                <Link to="/dashboard">
                  <Button className="bg-[#E91E8C] hover:bg-[#c21473] text-white text-sm" data-testid="dashboard-btn">Mi Dashboard</Button>
                </Link>
              ) : (
                <>
                  <Link to="/login" className="hidden sm:block">
                    <Button variant="ghost" className="text-slate-600 hover:text-[#0A2463]" data-testid="login-btn">Iniciar Sesion</Button>
                  </Link>
                  <Link to="/register">
                    <Button className="bg-[#E91E8C] hover:bg-[#c21473] text-white text-sm" data-testid="register-btn">Aplicar</Button>
                  </Link>
                </>
              )}
              <MobileMenuButton open={mobileOpen} onToggle={() => setMobileOpen(!mobileOpen)} />
            </div>
          </div>
          <MobileMenu
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            links={[
              { to: '/programs', label: 'Catalogo' },
              { to: '/edupro', label: 'EduPro' },
              { to: '/pre-aprobacion', label: 'Pre-Aprobacion' },
              { to: '/calculator', label: 'Calculadora' },
              { to: '/login', label: 'Iniciar Sesion' },
            ]}
          />
        </div>
      </nav>

      {/* ====== HERO ====== */}
      <section className="pt-24 sm:pt-28 pb-16 sm:pb-20 relative overflow-hidden bg-[#0A2463]">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(circle at 20% 80%, #E91E8C 0%, transparent 50%), radial-gradient(circle at 80% 20%, #E91E8C 0%, transparent 50%)' }}></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
          {/* Audience Toggle */}
          <div className="flex justify-center mb-10 sm:mb-12">
            <div className="bg-white/10 backdrop-blur-sm rounded-full p-1 inline-flex" data-testid="audience-toggle">
              <button
                onClick={() => setAudience('student')}
                className={`flex items-center gap-2 px-6 sm:px-8 py-2.5 rounded-full text-sm font-bold transition-all duration-300 ${
                  isStudent ? 'bg-[#E91E8C] text-white shadow-lg' : 'text-white/60 hover:text-white'
                }`}
                data-testid="toggle-student"
              >
                <GraduationCap className="w-4 h-4" />
                Soy Alumno
              </button>
              <button
                onClick={() => setAudience('institution')}
                className={`flex items-center gap-2 px-6 sm:px-8 py-2.5 rounded-full text-sm font-bold transition-all duration-300 ${
                  !isStudent ? 'bg-white text-[#0A2463] shadow-lg' : 'text-white/60 hover:text-white'
                }`}
                data-testid="toggle-institution"
              >
                <Building2 className="w-4 h-4" />
                Soy Institucion
              </button>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div key={audience}>
              {isStudent && (
                <div className="bg-white rounded-2xl p-3 inline-block mb-5 shadow-lg">
                  <SNPLLogo size="md" />
                </div>
              )}
              {!isStudent && (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-white/70 text-sm mb-5">
                  <Building2 className="w-4 h-4" /> Para Instituciones Educativas
                </div>
              )}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.05] mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {c.heroTitle}
              </h1>
              <p className="text-base sm:text-lg text-white/70 mb-8 max-w-lg leading-relaxed">{c.heroSub}</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to={c.ctaLink}>
                  <Button size="lg" className="bg-[#E91E8C] hover:bg-[#c21473] text-white text-base px-8 py-6 w-full sm:w-auto font-bold rounded-full shadow-lg shadow-[#E91E8C]/30" data-testid="hero-primary-cta">
                    {c.cta} <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to={c.secondaryLink}>
                  <Button size="lg" className="bg-white/10 text-white hover:bg-white/20 text-base px-8 py-6 w-full sm:w-auto font-bold rounded-full border border-white/20" data-testid="hero-secondary-cta">
                    {isStudent && <Zap className="mr-2 w-4 h-4" />}
                    {c.secondaryCta}
                  </Button>
                </Link>
              </div>
            </div>

            {/* Hero Stats Bento */}
            <div className="hidden lg:grid grid-cols-2 gap-3">
              {(isStudent
                ? [{ v: '+70%', l: 'Tasa de aprobacion' }, { v: '30s', l: 'Pre-aprobacion' }, { v: '$100K', l: 'Monto maximo MXN' }, { v: '24', l: 'Meses de plazo' }]
                : [{ v: '80%', l: 'Pago inmediato' }, { v: '0%', l: 'Riesgo crediticio' }, { v: '+40%', l: 'Mas inscripciones' }, { v: '24hrs', l: 'Desembolso' }]
              ).map((s, i) => (
                <div key={i} className="bg-white/[0.07] backdrop-blur-sm rounded-2xl p-5 text-center border border-white/10">
                  <div className="text-3xl sm:text-4xl font-black text-white mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{s.v}</div>
                  <div className="text-white/50 text-xs uppercase tracking-widest font-semibold">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
          {/* Mobile Stats */}
          <div className="lg:hidden grid grid-cols-2 gap-2 mt-8">
            {(isStudent
              ? [{ v: '+70%', l: 'Aprobacion' }, { v: '30s', l: 'Pre-aprobacion' }, { v: '$100K', l: 'Monto max' }, { v: '24mo', l: 'Plazo max' }]
              : [{ v: '80%', l: 'Pago inmediato' }, { v: '0%', l: 'Riesgo' }, { v: '+40%', l: 'Inscripciones' }, { v: '24hrs', l: 'Desembolso' }]
            ).map((s, i) => (
              <div key={i} className="bg-white/10 rounded-xl p-3 text-center border border-white/10">
                <div className="text-xl font-black text-white">{s.v}</div>
                <div className="text-white/50 text-[10px] uppercase tracking-wider">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== EDUPRO PARTNERSHIP ====== */}
      <section className="py-14 sm:py-20 bg-white relative overflow-hidden" data-testid="edupro-partnership">
        <div className="absolute -top-24 -right-24 w-80 h-80 bg-[#E91E8C]/5 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
          <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-[#0A2463] to-[#0d2f7a] text-white p-8 sm:p-12 shadow-xl">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-[#E91E8C] rounded-full px-4 py-1.5 mb-6">
                  <Star className="w-4 h-4 text-white" />
                  <span className="text-xs font-bold uppercase tracking-[0.15em]">Nueva alianza estratégica</span>
                </div>
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-3xl font-black" style={{ fontFamily: "'Outfit', sans-serif" }}>Ed.Fi</span>
                  <span className="text-white/40 text-3xl">×</span>
                  <div className="bg-white rounded-xl px-4 py-2.5">
                    <img src="/edupro-logo.png" alt="EduPro Global Education" className="h-9 w-auto" data-testid="edupro-landing-logo" />
                  </div>
                </div>
                <h2 className="text-3xl sm:text-4xl font-black tracking-tight leading-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Financia tu futuro en España con EduPro
                </h2>
                <p className="text-white/70 mb-6 max-w-lg">
                  Nos unimos a <span className="font-semibold text-white">EduPro Global Education</span> para financiar programas
                  internacionales de <span className="font-semibold text-white">1,800 USD</span> — pagaderos a 18–24 meses en pesos
                  mexicanos o colombianos, sin penalización por pago anticipado.
                </p>
                <div className="flex flex-wrap items-center gap-3 mb-8">
                  <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-3 py-1.5 text-sm"><span className="text-lg">🇲🇽</span> México</span>
                  <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-3 py-1.5 text-sm"><span className="text-lg">🇨🇴</span> Colombia</span>
                  <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-3 py-1.5 text-sm"><Globe className="w-4 h-4" /> Estudia en Madrid</span>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Link to="/edupro">
                    <Button className="bg-[#E91E8C] hover:bg-[#c21473] text-white px-7 py-6 text-base rounded-xl" data-testid="edupro-landing-cta">
                      Conocer el financiamiento EduPro <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Wallet, label: '1,800 USD', sub: 'por alumno' },
                  { icon: Timer, label: '18–24 meses', sub: 'plazo flexible' },
                  { icon: Percent, label: '2.5%–4%', sub: 'mensual s/ perfil' },
                  { icon: UserCheck, label: 'Con aval', sub: 'mejora tus tasas' },
                ].map((s, i) => (
                  <div key={i} className="bg-white/[0.07] rounded-2xl p-5 border border-white/10">
                    <s.icon className="w-6 h-6 text-[#E91E8C] mb-3" />
                    <p className="text-2xl font-black" style={{ fontFamily: "'Outfit', sans-serif" }}>{s.label}</p>
                    <p className="text-white/50 text-xs">{s.sub}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====== PROBLEM ====== */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#E91E8C] mb-3">El problema</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A2463] tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              La educacion tiene una barrera financiera
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            <div className="bg-[#0A2463] rounded-2xl p-8 text-center">
              <AlertTriangle className="w-8 h-8 text-[#E91E8C] mx-auto mb-4" />
              <div className="text-5xl font-black text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>40%</div>
              <p className="text-white/70 text-sm">de estudiantes <span className="text-white font-bold">no pueden inscribirse</span> por falta de financiamiento</p>
            </div>
            <div className="bg-[#0A2463] rounded-2xl p-8 text-center">
              <Building2 className="w-8 h-8 text-[#E91E8C] mx-auto mb-4" />
              <div className="text-5xl font-black text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>75%</div>
              <p className="text-white/70 text-sm">de instituciones <span className="text-white font-bold">no pueden ofrecer</span> financiamiento directo</p>
            </div>
            <div className="bg-[#0A2463] rounded-2xl p-8 text-center">
              <Globe className="w-8 h-8 text-[#E91E8C] mx-auto mb-4" />
              <div className="text-5xl font-black text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>132K</div>
              <p className="text-white/70 text-sm">instituciones de educacion superior en <span className="text-white font-bold">Latinoamerica</span></p>
            </div>
          </div>
        </div>
      </section>

      {/* ====== SOLUTION / PRODUCT ====== */}
      <section className="py-16 sm:py-20 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#E91E8C] mb-3">La solucion</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A2463] tracking-tight mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {isStudent ? 'Financiamiento a tu medida' : 'Cero riesgo para tu institucion'}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {isStudent
                ? 'Accede a credito educativo con las mejores condiciones del mercado'
                : 'Ofrece financiamiento a tus alumnos sin asumir cartera ni riesgo crediticio'}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {(isStudent ? [
              { icon: <Wallet className="w-6 h-6" />, title: '$10K - $100K MXN', desc: 'Tickets desde $500 hasta $5,000 USD para cualquier programa' },
              { icon: <Percent className="w-6 h-6" />, title: '5% mensual', desc: '10% de enganche. Pagos flexibles de 30 a 180 dias' },
              { icon: <Timer className="w-6 h-6" />, title: 'Decision instantanea', desc: 'Pre-aprobacion en 30 segundos con tu CURP. 100% digital' },
              { icon: <BadgeCheck className="w-6 h-6" />, title: 'Zero Risk Score', desc: 'Underwriting educativo con seguro para prestamos' },
            ] : [
              { icon: <Banknote className="w-6 h-6" />, title: 'Pago del 80% inmediato', desc: 'Recibe el pago de la colegiatura al momento de la inscripcion' },
              { icon: <Shield className="w-6 h-6" />, title: 'Cero riesgo crediticio', desc: 'Ed.Fi asume toda la cartera. Cero cobranza, cero cartera vencida' },
              { icon: <TrendingUp className="w-6 h-6" />, title: '+40% inscripciones', desc: 'Recupera alumnos que no se inscriben por falta de financiamiento' },
              { icon: <BarChart3 className="w-6 h-6" />, title: 'Dashboard digital', desc: 'Monitorea inscripciones, pagos y rendimiento en tiempo real' },
            ]).map((f, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-slate-200 hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: isStudent ? '#E91E8C15' : '#0A246315', color: isStudent ? '#E91E8C' : '#0A2463' }}>
                  {f.icon}
                </div>
                <h3 className="text-base font-bold text-slate-900 mb-1.5">{f.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== ECONOMICS / FLOW OF FUNDS ====== */}
      <section id="economia" className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#E91E8C] mb-3">Como funciona</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A2463] tracking-tight mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Flujo de fondos
            </h2>
            <p className="text-slate-600 max-w-xl mx-auto">Ejemplo con colegiatura de $50,000 MXN</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {/* Student */}
            <div className="rounded-2xl border-2 border-[#E91E8C]/20 bg-[#E91E8C]/[0.03] p-6 text-center">
              <div className="w-14 h-14 rounded-full bg-[#E91E8C]/10 flex items-center justify-center mx-auto mb-4">
                <GraduationCap className="w-7 h-7 text-[#E91E8C]" />
              </div>
              <h3 className="font-bold text-[#0A2463] mb-3 text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>Alumno</h3>
              <div className="space-y-2 text-sm text-left">
                <div className="flex justify-between"><span className="text-slate-500">Enganche (10%)</span><span className="font-bold text-slate-800">$5,000</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Interes mensual</span><span className="font-bold text-slate-800">5%</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Plazo flexible</span><span className="font-bold text-slate-800">3-24 meses</span></div>
              </div>
              <div className="mt-4 pt-3 border-t border-[#E91E8C]/10">
                <p className="text-xs text-slate-500">Paga en cuotas comodas</p>
              </div>
            </div>
            {/* Ed.Fi */}
            <div className="rounded-2xl border-2 border-[#0A2463]/20 bg-[#0A2463]/[0.03] p-6 text-center relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#0A2463] text-white text-xs font-bold px-3 py-1 rounded-full">Ed.Fi</div>
              <div className="w-14 h-14 rounded-full bg-[#0A2463]/10 flex items-center justify-center mx-auto mb-4 mt-2">
                <Landmark className="w-7 h-7 text-[#0A2463]" />
              </div>
              <h3 className="font-bold text-[#0A2463] mb-3 text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>Plataforma</h3>
              <div className="space-y-2 text-sm text-left">
                <div className="flex justify-between"><span className="text-slate-500">Descuento inst.</span><span className="font-bold text-green-600">$10,000</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Spread intereses</span><span className="font-bold text-green-600">$14,400</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Valor generado</span><span className="font-bold text-green-600">$24,400</span></div>
              </div>
              <div className="mt-4 pt-3 border-t border-[#0A2463]/10">
                <p className="text-xs text-slate-500">Gestiona credito y riesgo</p>
              </div>
            </div>
            {/* University */}
            <div className="rounded-2xl border-2 border-[#0A2463]/20 bg-[#0A2463]/[0.03] p-6 text-center">
              <div className="w-14 h-14 rounded-full bg-[#0A2463]/10 flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-7 h-7 text-[#0A2463]" />
              </div>
              <h3 className="font-bold text-[#0A2463] mb-3 text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>Universidad</h3>
              <div className="space-y-2 text-sm text-left">
                <div className="flex justify-between"><span className="text-slate-500">Recibe upfront</span><span className="font-bold text-slate-800">$40,000</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Riesgo crediticio</span><span className="font-bold text-green-600">0%</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Cobranza</span><span className="font-bold text-green-600">Ninguna</span></div>
              </div>
              <div className="mt-4 pt-3 border-t border-[#0A2463]/10">
                <p className="text-xs text-slate-500">Sin riesgo, pago garantizado</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====== COMPETITIVE BENCHMARK ====== */}
      <section className="py-16 sm:py-20 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#E91E8C] mb-3">Benchmark competitivo</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A2463] tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Por que Ed.Fi?
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left p-4 text-sm font-semibold text-slate-500"></th>
                  <th className="p-4 text-sm font-black text-white bg-[#0A2463] text-center" style={{ fontFamily: "'Outfit', sans-serif" }}>ed.fi</th>
                  <th className="p-4 text-sm font-semibold text-slate-500 text-center">Banca tradicional</th>
                  <th className="p-4 text-sm font-semibold text-slate-500 text-center">BNPL generico</th>
                  <th className="p-4 text-sm font-semibold text-slate-500 text-center hidden sm:table-cell">Plan interno inst.</th>
                </tr>
              </thead>
              <tbody>
                {benchmarkData.map((row, i) => (
                  <tr key={i} className="border-b border-slate-100 last:border-0">
                    <td className="p-4 text-sm font-semibold text-slate-700">{row.metric}</td>
                    <td className="p-4 text-sm font-bold text-[#0A2463] text-center bg-[#0A2463]/[0.03]">{row.edfi}</td>
                    <td className="p-4 text-sm text-slate-500 text-center">{row.traditional}</td>
                    <td className="p-4 text-sm text-slate-500 text-center">{row.bnpl}</td>
                    <td className="p-4 text-sm text-slate-500 text-center hidden sm:table-cell">{row.internal}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ====== PROGRAMS (Student mode) ====== */}
      {isStudent && (
        <section className="py-16 sm:py-20 bg-white" data-testid="marketplace-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10">
              <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#E91E8C] mb-3">Catalogo educativo</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A2463] tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Oferta educativa financiable
              </h2>
            </div>
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              <Link to="/programs">
                <Button variant="outline" size="sm" className="rounded-full border-[#0A2463] text-[#0A2463] hover:bg-[#0A2463] hover:text-white" data-testid="filter-all-programs">
                  Todos los programas
                </Button>
              </Link>
              {universities.map((uni) => (
                <Link key={uni.university_id} to={`/programs?university=${uni.university_id}`}>
                  <Button variant="outline" size="sm" className="rounded-full border-slate-300 text-slate-600 hover:border-[#0A2463] hover:text-[#0A2463]" data-testid={`filter-uni-${uni.university_id}`}>
                    {uni.name}
                  </Button>
                </Link>
              ))}
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {featuredPrograms.map((program) => (
                <Link
                  key={program.program_id}
                  to={`/programs/${program.program_id}`}
                  className="group bg-white rounded-2xl border border-slate-200 p-5 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 block"
                  data-testid={`featured-program-${program.program_id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-[#0A2463]/10 text-[#0A2463]">{program.category}</span>
                    <span className="px-2 py-1 rounded-full text-xs bg-slate-100 text-slate-500">
                      {program.modality === 'online' ? 'En linea' : program.modality === 'presencial' ? 'Presencial' : 'Hibrido'}
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-2 line-clamp-2 group-hover:text-[#0A2463] transition-colors">{program.name}</h3>
                  <div className="flex items-center gap-1.5 text-sm text-slate-500 mb-3">
                    <Building2 className="w-3.5 h-3.5" /> {program.university_name}
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <span className="flex items-center gap-1 text-sm text-slate-500"><Clock className="w-3.5 h-3.5" /> {program.duration_months}mo</span>
                    <span className="text-base font-bold text-[#0A2463]">{formatPrice(program.price)}</span>
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/programs">
                <Button className="bg-[#0A2463] hover:bg-[#071946] text-white rounded-full px-8" data-testid="view-all-programs-btn">
                  Ver Todos los Programas <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ====== GLOBAL SNPL VALIDATION ====== */}
      <section className="py-14 sm:py-16 bg-[#0A2463]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-center text-white/50 text-xs uppercase tracking-[0.2em] font-semibold mb-8">Study Now Pay Later en el mundo</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              { name: 'Flywire', stat: '$3B market cap', detail: '+2,000 instituciones aliadas, IPO 2021' },
              { name: 'Prodigy Finance', stat: '$2B en fondeo', detail: '+850 instituciones, +20K alumnos financiados' },
              { name: 'Isaac', stat: '$125M raised', detail: 'Adquirido por Arco Educacao, lider en Brasil' },
            ].map((p, i) => (
              <div key={i} className="bg-white/[0.06] rounded-2xl p-6 border border-white/10 text-center">
                <p className="text-xl font-black text-white mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{p.name}</p>
                <p className="text-[#E91E8C] font-bold text-sm mb-2">{p.stat}</p>
                <p className="text-white/50 text-xs">{p.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== MARKET OPPORTUNITY ====== */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#E91E8C] mb-3">Oportunidad de mercado</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A2463] tracking-tight mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
                $170B USD en educacion LATAM
              </h2>
              <p className="text-slate-600 mb-6">Ed.Fi es la primera fintech SNPL enfocada en educacion en Latinoamerica. Un mercado con $13.6B USD en revenue potencial.</p>
              <div className="space-y-4">
                {[
                  { country: 'Mexico', students: '4.7M', detail: 'universitarios + 1.4M ed. no regulada' },
                  { country: 'Colombia', students: '2.3M', detail: 'universitarios + 650K ed. no regulada' },
                  { country: 'Chile', students: '1.3M', detail: 'universitarios, $8K ticket promedio' },
                ].map((m, i) => (
                  <div key={i} className="flex items-center gap-4 bg-slate-50 rounded-xl p-4">
                    <div className="w-10 h-10 bg-[#0A2463] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Globe className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-900 text-sm">{m.country} — {m.students} estudiantes</p>
                      <p className="text-xs text-slate-500">{m.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#0A2463] rounded-2xl p-8 text-center col-span-2">
                  <p className="text-6xl font-black text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>$170B</p>
                  <p className="text-white/60 text-sm mt-1">TAM - Mercado educativo LATAM</p>
                </div>
                <div className="bg-slate-50 rounded-2xl p-6 text-center border border-slate-200">
                  <p className="text-3xl font-black text-[#0A2463]" style={{ fontFamily: "'Outfit', sans-serif" }}>$13.6B</p>
                  <p className="text-slate-500 text-xs mt-1">Revenue potencial</p>
                </div>
                <div className="bg-slate-50 rounded-2xl p-6 text-center border border-slate-200">
                  <p className="text-3xl font-black text-[#E91E8C]" style={{ fontFamily: "'Outfit', sans-serif" }}>1st</p>
                  <p className="text-slate-500 text-xs mt-1">Fintech SNPL en LATAM</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====== PARTNERS ====== */}
      <section className="py-14 sm:py-16 bg-[#FAFAFA] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-center text-slate-400 text-xs uppercase tracking-[0.2em] font-semibold mb-8">Instituciones aliadas y en prospeccion</p>
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-4 items-center">
            {partners.map((p, i) => (
              <span key={i} className="text-slate-400 font-bold text-sm hover:text-[#0A2463] transition-colors cursor-default">{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ====== TEAM ====== */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#E91E8C] mb-3">Nuestro equipo</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A2463] tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Liderazgo con experiencia
            </h2>
            <p className="text-slate-500 text-sm">Respaldados por Ergo Capital</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-8 max-w-3xl mx-auto">
            {[
              { name: 'Ernesto Gomez', role: 'Co-Founder & CEO', img: 'https://images.unsplash.com/photo-1762522926157-bcc04bf0b10a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGhlYWRzaG90JTIwcG9ydHJhaXQlMjBjbGVhbiUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc2MTg2MDY2fDA&ixlib=rb-4.1.0&q=85' },
              { name: 'Mariana Guillen', role: 'Co-Founder & CGO', img: 'https://images.unsplash.com/photo-1762505464553-1f4eb1578f23?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGhlYWRzaG90JTIwcG9ydHJhaXQlMjBjbGVhbiUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc2MTg2MDY2fDA&ixlib=rb-4.1.0&q=85' },
              { name: 'Tania Corona', role: 'Co-Founder & CFO', img: 'https://images.unsplash.com/photo-1762522927402-f390672558d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGhlYWRzaG90JTIwcG9ydHJhaXQlMjBjbGVhbiUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc2MTg2MDY2fDA&ixlib=rb-4.1.0&q=85' },
            ].map((m, i) => (
              <div key={i} className="text-center">
                <div className="w-28 h-28 rounded-full overflow-hidden mx-auto mb-4 border-4 border-slate-100">
                  <img src={m.img} alt={m.name} className="w-full h-full object-cover" />
                </div>
                <h3 className="font-bold text-[#0A2463] text-base">{m.name}</h3>
                <p className="text-sm text-slate-500">{m.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== CTA ====== */}
      <section id="instituciones" className="py-16 sm:py-20 bg-[#0A2463]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {isStudent ? 'Listo para comenzar?' : 'Unete a la red Ed.Fi'}
          </h2>
          <p className="text-white/60 mb-8 max-w-lg mx-auto">
            {isStudent
              ? 'Aplica en minutos y recibe tu pre-aprobacion de forma inmediata'
              : 'Ofrece financiamiento a tus alumnos. Proceso 100% digital, cero riesgo para tu institucion'}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={c.ctaLink}>
              <Button size="lg" className="bg-[#E91E8C] hover:bg-[#c21473] text-white text-base px-10 py-6 font-bold rounded-full shadow-lg shadow-[#E91E8C]/30" data-testid="apply-now-btn">
                {c.cta} <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/calculator">
              <Button size="lg" className="bg-white/10 hover:bg-white/20 text-white text-base px-10 py-6 rounded-full border border-white/20">
                <FileText className="mr-2 w-5 h-5" /> Simular Credito
              </Button>
            </Link>
            <a href="/edfi-sales-deck-2026.pdf" download="Ed.Fi Sales Deck 2026.pdf">
              <Button size="lg" className="bg-white/10 hover:bg-white/20 text-white text-base px-10 py-6 rounded-full border border-white/20" data-testid="download-deck-btn">
                <FileText className="mr-2 w-5 h-5" /> Descargar Deck
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* ====== CONTACT ====== */}
      <section className="py-12 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 text-center">
            {[
              { label: 'Universidades e Institutos', email: 'institutions@edfi.la' },
              { label: 'Seguimiento de solicitudes', email: 'applications@edfi.la' },
              { label: 'Inversionistas', email: 'investors@edfi.la' },
              { label: 'Sitio web', email: 'www.edfi.la' },
            ].map((c, i) => (
              <div key={i}>
                <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">{c.label}</p>
                <p className="text-sm font-bold text-[#0A2463]">{c.email}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== FOOTER ====== */}
      <footer className="py-10 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <EdFiLogo size="md" linkTo="/" className="brightness-0 invert mb-3" />
              <p className="text-slate-400 text-sm">Study Now. Pay Later.<br />Financiamiento educativo en LATAM.</p>
            </div>
            <div>
              <h4 className="font-bold mb-3 text-sm">Producto</h4>
              <ul className="space-y-1.5 text-slate-400 text-sm">
                <li><Link to="/programs" className="hover:text-white transition-colors">Catalogo</Link></li>
                <li><Link to="/calculator" className="hover:text-white transition-colors">Calculadora</Link></li>
                <li><Link to="/pre-aprobacion" className="hover:text-white transition-colors">Pre-Aprobacion</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-3 text-sm">Empresa</h4>
              <ul className="space-y-1.5 text-slate-400 text-sm">
                <li><a href="#instituciones" className="hover:text-white transition-colors">Para Instituciones</a></li>
                <li><a href="https://edfi.la" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">edfi.la</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-3 text-sm">Legal</h4>
              <ul className="space-y-1.5 text-slate-400 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Terminos y Condiciones</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Aviso de Privacidad</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-6 text-center text-slate-500 text-sm">
            <p>&copy; 2026 ed.fi. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
